import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Lock, ShieldCheck, Briefcase, BarChart3, Globe, Database, ArrowRight, Loader2 } from "lucide-react";

const HERO = "https://images.pexels.com/photos/9219074/pexels-photo-9219074.jpeg";

const FEATURES = [
  { icon: Database, label: "Catalogo SKU", desc: "Vini, lotti, schede tecniche" },
  { icon: ShieldCheck, label: "Compliance MAPA", desc: "Brasile · Estrazione AI" },
  { icon: Briefcase, label: "Quotation Desk", desc: "Cost simulation EXW/FOB" },
  { icon: BarChart3, label: "Dashboard KPI", desc: "Procurement · Logistics · Sales" },
];

export default function GestionaleLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  // If already logged in with the right role → redirect immediately
  useEffect(() => {
    if (!user) return;
    if (["super_admin", "buyer", "admin_broker"].includes(user.role)) {
      nav("/gestionale/dashboard", { replace: true });
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      const allowed = ["super_admin", "buyer", "admin_broker"];
      if (!allowed.includes(u.role)) {
        toast.error("Questo account non ha accesso al gestionale B2B");
        return;
      }
      toast.success(`Bentornato, ${u.name}`);
      nav("/gestionale/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  if (user && (user.role === "super_admin" || user.role === "buyer" || user.role === "admin_broker")) {
    return <Navigate to="/gestionale/dashboard" replace />;
  }

  return (
    <div data-testid="gestionale-login-page" className="min-h-screen bg-[#0a0a0a] text-white grid md:grid-cols-[1fr_minmax(380px,520px)]">
      {/* LEFT — Brand panel */}
      <div className="relative hidden md:flex flex-col justify-between p-12 lg:p-16 overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#0a0a0a]/85 to-[#1a0608]/70" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 border border-[#C9A961] flex items-center justify-center">
              <span className="editorial-heading text-[#C9A961] text-xl">A</span>
            </div>
            <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-bold">Amazonico 3000 · Gestionale</div>
          </div>
        </div>

        <div className="relative z-10 max-w-xl">
          <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-6 flex items-center gap-3">
            <div className="h-px w-10 bg-[#C9A961]" /> B2B Operations Suite
          </div>
          <h1 className="editorial-heading text-5xl lg:text-7xl leading-[0.95] font-light">
            Il gestionale<br /><span className="italic text-[#C9A961]">d'intermediazione</span><br />Italia → Brasile.
          </h1>
          <p className="mt-8 text-white/70 text-lg leading-relaxed max-w-lg">
            Anagrafica fornitori europei, compliance MAPA, consolidamento container, quotation desk e CRM B2B in un'unica console operativa.
          </p>

          <div className="mt-12 grid grid-cols-2 gap-4 max-w-md">
            {FEATURES.map((f, i) => (
              <div key={i} className="border-l-2 border-[#C9A961]/40 pl-4">
                <f.icon className="h-4 w-4 text-[#C9A961] mb-2" />
                <div className="text-sm font-semibold uppercase tracking-wider">{f.label}</div>
                <div className="text-xs text-white/50 mt-0.5">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-4 text-[10px] uppercase tracking-[0.3em] text-white/40">
          <Globe className="h-3 w-3" /> Connessione cifrata
          <span className="h-px w-6 bg-white/20" />
          <Lock className="h-3 w-3" /> Sessione protetta
        </div>
      </div>

      {/* RIGHT — Login form */}
      <div className="flex items-center justify-center p-6 md:p-10 bg-gradient-to-b from-[#0a0a0a] to-[#1a0608] border-l border-[#C9A961]/20">
        <div className="w-full max-w-sm">
          {/* Mobile header */}
          <div className="md:hidden mb-8 flex items-center gap-3">
            <div className="h-10 w-10 border border-[#C9A961] flex items-center justify-center">
              <span className="editorial-heading text-[#C9A961] text-xl">A</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961] font-bold">Amazonico 3000<br /><span className="text-white/60">Gestionale B2B</span></div>
          </div>

          <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-bold mb-3">
            Accesso riservato
          </div>
          <h2 className="editorial-heading text-3xl md:text-4xl text-white">
            Accedi al<br /><span className="italic">gestionale.</span>
          </h2>
          <p className="text-sm text-white/50 mt-3 leading-relaxed">
            Riservato a Super Admin, Broker e Buyer B2B autorizzati. Tutti gli accessi sono tracciati.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label htmlFor="g-email" className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-bold">Email aziendale</label>
              <Input
                data-testid="gestionale-email"
                id="g-email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@amazonico3000.com"
                className="mt-1.5 bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:border-[#C9A961] focus-visible:ring-[#C9A961]/30 h-11 rounded-sm"
              />
            </div>
            <div>
              <label htmlFor="g-password" className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-bold">Password</label>
              <Input
                data-testid="gestionale-password"
                id="g-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className="mt-1.5 bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:border-[#C9A961] focus-visible:ring-[#C9A961]/30 h-11 rounded-sm"
              />
            </div>
            <Button
              data-testid="gestionale-submit"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#C9A961] text-black hover:bg-white font-bold uppercase tracking-[0.2em] text-xs rounded-sm gap-2 mt-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {loading ? "Verifica…" : "Entra nel gestionale"}
              {!loading && <ArrowRight className="h-4 w-4" />}
            </Button>
          </form>

          <button
            type="button"
            data-testid="gestionale-toggle-demo"
            onClick={() => setShowDemo(!showDemo)}
            className="mt-6 text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-[#C9A961]"
          >
            {showDemo ? "Nascondi" : "Mostra"} credenziali demo →
          </button>

          {showDemo && (
            <div data-testid="gestionale-demo-creds" className="mt-3 p-4 border border-[#C9A961]/30 bg-[#C9A961]/5 rounded-sm space-y-3 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961] font-bold mb-1">Super Admin (Gestionale)</div>
                <div className="font-mono text-white/80">gestionale@amazonico3000.com</div>
                <div className="font-mono text-white/80">Gestionale@A3K-2026!</div>
              </div>
              <div className="h-px bg-white/10" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961] font-bold mb-1">Buyer B2B</div>
                <div className="font-mono text-white/80">rivenditore@amazonico3000.com</div>
                <div className="font-mono text-white/80">Rivenditore2026!</div>
              </div>
              <div className="h-px bg-white/10" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961] font-bold mb-1">Admin Broker</div>
                <div className="font-mono text-white/80">broker@terraitalia.com</div>
                <div className="font-mono text-white/80">Broker2026!</div>
              </div>
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-white/10 text-[10px] uppercase tracking-[0.25em] text-white/30 leading-relaxed">
            Hai dimenticato la password? Contatta il responsabile sistema.<br />
            Accessi tracciati ai fini audit.
          </div>
        </div>
      </div>
    </div>
  );
}
